import { useState, CSSProperties } from 'react';

interface DiagnosticsOverrideModalProps {
  /** e.g. "CLEOPATRA-03" — which PC the override applies to. */
  contextLine: string;
  /** Prefill for the operator name field (from the logged-in operator). */
  defaultOperator: string;
  onCancel: () => void;
  /** Must not throw — the parent handles success/failure feedback (toasts). */
  onSubmit: (operator: string, reason: string) => Promise<void>;
}

/**
 * Custom modal for overriding a failed diagnostics run — window.prompt is a
 * silent no-op in Electron, so all operator input goes through this. Styling
 * clones MarkIssueModal (fixed backdrop + centered white card). Both fields
 * are required by the agent's POST /api/diagnostics/override.
 */
export default function DiagnosticsOverrideModal({
  contextLine,
  defaultOperator,
  onCancel,
  onSubmit,
}: DiagnosticsOverrideModalProps) {
  const [operator, setOperator] = useState(defaultOperator);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = operator.trim().length > 0 && reason.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(operator.trim(), reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#54626f',
    margin: '14px 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };
  const fieldStyle: CSSProperties = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d5dce5',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
    color: '#16202e',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(16,32,46,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => {
        if (!submitting) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 24,
          width: 'min(460px, calc(100vw - 32px))',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          color: '#16202e',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0 }}>Override checks &amp; unlock</h3>
        <div style={{ fontSize: 13, color: '#54626f', marginTop: 4 }}>{contextLine}</div>
        <div
          style={{
            marginTop: 12,
            padding: '8px 10px',
            borderRadius: 8,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.35)',
            color: '#b3261e',
            fontSize: 13,
          }}
        >
          This unlocks launches on a PC whose self-test failed. The override is
          recorded with your name and reason.
        </div>

        <label style={labelStyle}>Operator *</label>
        <input
          type="text"
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          disabled={submitting}
          placeholder="Your name"
          style={fieldStyle}
        />

        <label style={labelStyle}>Reason *</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          rows={3}
          placeholder="Why is it safe to launch anyway?"
          style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="refresh-btn" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            className="refresh-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              opacity: !canSubmit ? 0.6 : 1,
            }}
          >
            {submitting ? 'Overriding…' : 'Override & unlock'}
          </button>
        </div>
      </div>
    </div>
  );
}
